/**
 * Hit detection and intersection testing for canvas objects
 */

import { Vec2, distanceBetween, pointInRect, pointInCircle } from './coordinates';
import type { StageObject, ShootingPosition, Wall, StageLayout } from '@/stage/types';

/**
 * Rotate a point around a center by `angleRad` (clockwise in screen-space,
 * counter-clockwise in math convention — matches the renderer's ctx.rotate).
 * Returns the rotated point.
 */
function rotateAround(point: Vec2, center: Vec2, angleRad: number): Vec2 {
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  };
}

/**
 * Extract the 2D rotation (radians) of a StageObject. The renderer treats
 * `rotation.z` as degrees — mirror that here so hit detection stays aligned
 * with what's drawn on screen.
 */
function getObjectRotation2D(obj: StageObject): number {
  return (obj.rotation.z * Math.PI) / 180;
}

/** Corners (world space) of a rotated, axis-sized rectangle. */
function obbCorners(
  center: Vec2,
  halfWidth: number,
  halfHeight: number,
  angleRad: number,
): Vec2[] {
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  const local: Vec2[] = [
    { x: -halfWidth, y: -halfHeight },
    { x: halfWidth, y: -halfHeight },
    { x: halfWidth, y: halfHeight },
    { x: -halfWidth, y: halfHeight },
  ];
  return local.map((p) => ({
    x: center.x + p.x * cos - p.y * sin,
    y: center.y + p.x * sin + p.y * cos,
  }));
}

/**
 * Get the bounding dimensions of a stage object based on its type
 * Returns world-space dimensions in meters
 */
function getObjectDimensions(
  obj: StageObject
): { width: number; height: number; radius?: number } {
  const type = obj.type;
  const variant = obj.variant || 'standard';

  // Paper targets — dimensions include head + body so the hit area matches
  // the renderer's total silhouette.
  if (type === 'paper_target') {
    if (variant === 'ipsc_classic') return { width: 0.457, height: 0.584 + 0.178 };
    if (variant === 'ipsc_mini') return { width: 0.3, height: 0.4 + 0.12 };
    if (variant === 'idpa') return { width: 0.432, height: 0.610 + 0.178 };
    if (variant === 'uspsa_metric') return { width: 0.46, height: 0.61 + 0.178 };
    if (variant === 'uspsa_mini') return { width: 0.31, height: 0.41 + 0.12 };
    return { width: 0.457, height: 0.584 + 0.178 };
  }

  // Steel targets - circles
  if (type === 'steel_target') {
    const inchesToMeters = 0.0254;
    if (variant === 'round_8') return { width: 8 * inchesToMeters, height: 8 * inchesToMeters, radius: 4 * inchesToMeters };
    if (variant === 'round_10') return { width: 10 * inchesToMeters, height: 10 * inchesToMeters, radius: 5 * inchesToMeters };
    if (variant === 'round_12') return { width: 12 * inchesToMeters, height: 12 * inchesToMeters, radius: 6 * inchesToMeters };
    // default 8"
    return { width: 8 * inchesToMeters, height: 8 * inchesToMeters, radius: 4 * inchesToMeters };
  }

  // Poppers - tall thin rectangle
  if (type === 'popper') {
    return { width: 0.1, height: 0.5 };
  }

  // No-shoots
  if (type === 'no_shoot') {
    return { width: 0.2, height: 0.3 };
  }

  // Barrels — same top-down footprint regardless of stack / half variant
  if (type === 'barrel') {
    return { width: 0.58, height: 0.58, radius: 0.29 };
  }

  // Tables — low variant has a smaller footprint
  if (type === 'table') {
    if (variant === 'low') return { width: 1.0, height: 0.6 };
    return { width: 1.5, height: 0.8 };
  }

  // Cones — 14" traffic cone base
  if (type === 'cone') {
    return { width: 0.36, height: 0.36, radius: 0.18 };
  }

  // Plates, swingers, drop turners, texas star, bobber (steel)
  if (type === 'plate' || type === 'swinger' || type === 'drop_turner' || type === 'texas_star' || type === 'bobber') {
    const inchesToMeters = 0.0254;
    return { width: 8 * inchesToMeters, height: 8 * inchesToMeters, radius: 4 * inchesToMeters };
  }

  // IDPA paper variants on swingers / popups — same silhouette footprint as
  // the static paper target so the hit box matches what's drawn.
  if (type === 'paper_swinger' || type === 'paper_swinger_once' || type === 'paper_popup') {
    return { width: 0.432, height: 0.610 + 0.178 };
  }

  // Fault lines - thin rectangle
  if (type === 'fault_line') {
    return { width: 0.05, height: 1 };
  }

  // Covers — thin panel. Bump the clickable depth a bit above the visual 4 cm
  // so picking the thin line isn't pixel-precise.
  if (type === 'hard_cover' || type === 'soft_cover') {
    return { width: 1, height: 0.15 };
  }

  // Furniture props — footprints match the 2D symbols / 3D models.
  if (type === 'chair') {
    return { width: 0.45, height: 0.5 };
  }
  if (type === 'door') {
    return { width: 1.0, height: 0.2 };
  }
  if (type === 'window') {
    return { width: 1.2, height: 0.15 };
  }
  if (type === 'bed') {
    return { width: 1.0, height: 2.0 };
  }
  if (type === 'couch') {
    return { width: 1.8, height: 0.85 };
  }
  if (type === 'car') {
    return { width: 4.4, height: 1.8 };
  }

  // Shooter — top-down: seated pose has a chair behind so the footprint is
  // deeper; standing poses are roughly shoulder-width plus a bit for props.
  if (type === 'shooter') {
    if (variant === 'shooter_seated') return { width: 0.6, height: 1.1 };
    if (variant === 'shooter_broom') return { width: 0.6, height: 1.3 };
    return { width: 0.55, height: 0.45 };
  }

  // Default
  return { width: 0.5, height: 0.5 };
}

/**
 * Test if a world position hits a stage object
 * Returns the object ID if hit, null otherwise
 */
export function hitTestObjects(worldPos: Vec2, objects: StageObject[]): string | null {
  // Test in reverse order (top to bottom for rendering)
  for (let i = objects.length - 1; i >= 0; i--) {
    const obj = objects[i];
    const dims = getObjectDimensions(obj);
    const objPos: Vec2 = { x: obj.position.x, y: obj.position.y };

    // Circular objects are rotation-invariant
    if (dims.radius !== undefined) {
      if (pointInCircle(worldPos, objPos, dims.radius)) {
        return obj.id;
      }
      continue;
    }

    // OBB hit test: rotate the mouse point into the object's local frame,
    // then test against the axis-aligned bounds at the origin.
    const angle = getObjectRotation2D(obj);
    const local = rotateAround(worldPos, objPos, -angle);
    const halfWidth = dims.width / 2;
    const halfHeight = dims.height / 2;
    if (
      pointInRect(
        local,
        { x: objPos.x - halfWidth, y: objPos.y - halfHeight },
        { x: objPos.x + halfWidth, y: objPos.y + halfHeight },
      )
    ) {
      return obj.id;
    }
  }

  return null;
}

/**
 * Test if a world position hits a shooting position
 */
export function hitTestPositions(
  worldPos: Vec2,
  positions: ShootingPosition[]
): string | null {
  // Test in reverse order
  for (let i = positions.length - 1; i >= 0; i--) {
    const pos = positions[i];
    const posWorldPos: Vec2 = { x: pos.position.x, y: pos.position.y };
    const halfWidth = pos.width_m / 2;
    const halfDepth = pos.depth_m / 2;

    // Simple AABB check - ignoring rotation for now
    if (
      pointInRect(
        worldPos,
        { x: posWorldPos.x - halfWidth, y: posWorldPos.y - halfDepth },
        { x: posWorldPos.x + halfWidth, y: posWorldPos.y + halfDepth }
      )
    ) {
      return pos.id;
    }
  }

  return null;
}

/**
 * Test if a world position hits a wall
 */
export function hitTestWalls(worldPos: Vec2, walls: Wall[]): string | null {
  const WALL_HIT_DISTANCE = 0.2; // meters

  for (let i = walls.length - 1; i >= 0; i--) {
    const wall = walls[i];

    // For walls with multiple points, check distance to each segment
    for (let j = 0; j < wall.points.length - 1; j++) {
      const p1: Vec2 = { x: wall.points[j].x, y: wall.points[j].y };
      const p2: Vec2 = { x: wall.points[j + 1].x, y: wall.points[j + 1].y };

      const dist = distanceToLineSegment(worldPos, p1, p2);
      if (dist <= WALL_HIT_DISTANCE) {
        return wall.id;
      }
    }
  }

  return null;
}

/**
 * Test which element (object, position, wall, or annotation) is hit at a world position
 * Returns the first hit in priority order
 */
export function hitTestAll(
  worldPos: Vec2,
  layout: StageLayout
): { type: 'object' | 'position' | 'wall' | 'annotation'; id: string } | null {
  // Priority: objects -> positions -> walls -> annotations
  const objId = hitTestObjects(worldPos, layout.objects);
  if (objId) return { type: 'object', id: objId };

  const posId = hitTestPositions(worldPos, layout.shooting_positions);
  if (posId) return { type: 'position', id: posId };

  const wallId = hitTestWalls(worldPos, layout.walls);
  if (wallId) return { type: 'wall', id: wallId };

  // Annotations are text-based, harder to hit test
  // For now, we'll skip them in hitTestAll
  return null;
}

/**
 * Get all objects within a selection rectangle
 */
export function getObjectsInRect(
  topLeft: Vec2,
  bottomRight: Vec2,
  objects: StageObject[]
): string[] {
  const selected: string[] = [];

  for (const obj of objects) {
    const objPos: Vec2 = { x: obj.position.x, y: obj.position.y };
    const dims = getObjectDimensions(obj);

    // Use the axis-aligned bounds of the rotated footprint so the rubber-band
    // selection matches what the user sees for rotated objects.
    let objLeft: number;
    let objRight: number;
    let objTop: number;
    let objBottom: number;

    if (dims.radius !== undefined) {
      objLeft = objPos.x - dims.radius;
      objRight = objPos.x + dims.radius;
      objTop = objPos.y - dims.radius;
      objBottom = objPos.y + dims.radius;
    } else {
      const corners = obbCorners(
        objPos,
        dims.width / 2,
        dims.height / 2,
        getObjectRotation2D(obj),
      );
      objLeft = Math.min(...corners.map((c) => c.x));
      objRight = Math.max(...corners.map((c) => c.x));
      objTop = Math.min(...corners.map((c) => c.y));
      objBottom = Math.max(...corners.map((c) => c.y));
    }

    if (
      objRight >= topLeft.x &&
      objLeft <= bottomRight.x &&
      objBottom >= topLeft.y &&
      objTop <= bottomRight.y
    ) {
      selected.push(obj.id);
    }
  }

  return selected;
}

/**
 * Get all positions within a selection rectangle
 */
export function getPositionsInRect(
  topLeft: Vec2,
  bottomRight: Vec2,
  positions: ShootingPosition[]
): string[] {
  const selected: string[] = [];

  for (const pos of positions) {
    const posPos: Vec2 = { x: pos.position.x, y: pos.position.y };
    const left = posPos.x - pos.width_m / 2;
    const right = posPos.x + pos.width_m / 2;
    const top = posPos.y - pos.depth_m / 2;
    const bottom = posPos.y + pos.depth_m / 2;

    if (
      right >= topLeft.x &&
      left <= bottomRight.x &&
      bottom >= topLeft.y &&
      top <= bottomRight.y
    ) {
      selected.push(pos.id);
    }
  }

  return selected;
}

/**
 * Calculate the shortest distance from a point to a line segment
 */
function distanceToLineSegment(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSquared = dx * dx + dy * dy;

  if (lenSquared === 0) {
    // Segment is a point
    return distanceBetween(p, a);
  }

  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSquared;
  t = Math.max(0, Math.min(1, t));

  const closest: Vec2 = {
    x: a.x + t * dx,
    y: a.y + t * dy,
  };

  return distanceBetween(p, closest);
}
